import React from "react";

import { createSelector } from "@reduxjs/toolkit";
import { useSelector } from "react-redux";
import { useHistory, Link } from "react-router-dom";
import { reduxForm, SubmissionError } from "redux-form";
import focusFirstError from "Utils/focusFirstError";
import { isPartner } from "Utils/isPartner";

import { useUpdateApplicationMutation, getBorApplication } from "../api";
import BORPage from "../components/BORPage";
import { CompanyName, ContactName, ContactTitle, Email, PhoneNumber, Address1, City, State, Zip } from "../components/CommonQuestions";
import { baseURL, landingPageURL, currentPlanURL } from "../urls";
import { H4, Title, Subtitle } from "Cobalt/Header";
import BodyText from "Common/BodyText";
import Button from "Common/Button";
import { Row, Col } from "Common/Grid";
import Icon from "Common/Icon";

import makeBem from "Utils/makeBem";

const bem = makeBem("BORMainContent");

const withReduxForm = reduxForm({
  form: "companyInformation",
  destroyOnUnmount: false,
  enableReinitialize: true,
  onSubmit: async (values, _dispatch, props) => {
    const { ...borApplicationValues } = values;
    try {
      await props.updateBorApplication(borApplicationValues).unwrap();
    } catch (e) {
      const {
        data: { error, fieldErrors },
      } = e;
      throw new SubmissionError({
        _error: error,
        ...fieldErrors,
      });
    }
  },
  onSubmitFail: (errors) => {
    focusFirstError(errors, ".");
  },
  onSubmitSuccess: (_values, _dispatch, { history }) => {
    history.push(`${baseURL}${currentPlanURL}`);
  },
});

const CompanyInformationForm = withReduxForm(({ handleSubmit, submitting }) => (
  <form className={bem("form")} onSubmit={handleSubmit}>
    {/** Authorized Representative Form * */}
    <section>
      <Row className={bem("form", "titleSection")}>
        <Col>
          <Title className={bem("form", "title")}>Authorized representative information</Title>
          <BodyText className={bem("form", "subtitle")}>
            You must be an Authorized Representative to approve this broker of record change. <br />
            Please ensure the contact information below is for an authorized representative.
          </BodyText>
        </Col>
      </Row>
      <Row>
        <CompanyName />
      </Row>
      <Row>
        <ContactName />
      </Row>
      <Row>
        <ContactTitle />
      </Row>
      <Row>
        <Col lg={5} className={bem("form", "col")}>
          <Email sm={6} lg={12} />
        </Col>
        <Col lg={7} className={bem("form", "col")}>
          <PhoneNumber sm={6} lg={12} />
        </Col>
      </Row>
    </section>

    {/** Company Information Form * */}
    <section>
      <Row className={bem("form", "titleSection")}>
        <Col>
          <Title className={bem("formTitle")}>Company information</Title>
        </Col>
      </Row>
      <Row>
        <Address1 />
      </Row>
      <Row>
        <Col sm={4} className={bem("form", "col")}>
          <City />
        </Col>
        <Col sm={4} className={bem("form", "col")}>
          <State />
        </Col>
        <Col sm={4} className={bem("form", "col")}>
          <Zip />
        </Col>
      </Row>
    </section>
    {/** Submit * */}
    <Row>
      <Col smOffset={8} mdOffset={9}>
        <Button fullWidth submitting={submitting} disabled={submitting} size="medium" type="submit">
          Next
        </Button>
      </Col>
    </Row>
  </form>
));

const formInitialValues = createSelector(getBorApplication, (borApplication) => {
  const { name, contactName, contactTitle, email, contactPhoneNumber, address1, state, city, zip } = borApplication || {};
  return {
    name,
    contactName,
    contactTitle,
    email,
    contactPhoneNumber,
    address1,
    state,
    city,
    zip,
  };
});

const CompanyInformationPage = () => {
  const initialValues = useSelector(formInitialValues);
  const [updateBorApplication] = useUpdateApplicationMutation();
  const history = useHistory();

  return (
    <BORPage>
      <Button component={Link} size="medium" to={`${baseURL}${landingPageURL}`} color="link" className={bem("backButton")} pl={0}>
        <span>
          <Icon icon="chevron-left" pr={3} />
          Back
        </span>
      </Button>
      <H4 className={bem("title")}>Company Information</H4>
      {isPartner() && <Subtitle className={bem("subtitle")}>Nice! We already have these filled for you.</Subtitle>}
      <hr className={bem("divider")} />
      <CompanyInformationForm updateBorApplication={updateBorApplication} history={history} initialValues={initialValues} />
    </BORPage>
  );
};

export default CompanyInformationPage;
